/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useState } from 'react';

// Mantine
import {
	Alert,
	Avatar,
	Box,
	Divider,
	Flex,
	Group,
	Image,
	NumberFormatter,
	Paper,
	Skeleton,
	Spoiler,
	Stack,
	Tabs,
} from '@mantine/core';
import {
	useElementSize,
	useMediaQuery,
} from '@mantine/hooks';

// Styles
import classes from './additionality-project-details.module.css';
import classesTabs from './tabs.module.css';
// Router
import { useParams } from 'react-router';

// I18n
import { useTranslation } from 'react-i18next';

// Api

// Components

// Icons
import {
	IconInfoCircle,
	IconQuestionMark,
} from '@tabler/icons-react';

import './tabs.css';

// constants
import { DescriptionRenderer } from '@components/atoms/DescriptionRenderer';
import { Button, Title } from '@components/v2/ui';
import { Badge } from '@components/v2/ui/Badge';
import { IconProjectLocation } from '@components/v2/ui/IconProjectLocation/IconProjectLocation';
import { StatusSwitch } from '@components/v2/ui/StatusSwitch';
import { ACLS } from '@config/constants/acls';
import { NOT_APPLICABLE } from '@config/constants/common';
import { ROLES } from '@config/constants/roles';
import { useAdditionalityProject } from '@features/catalogs/additionality-projects/details/api/get-additionality-project-by-id';
import { useActivateAdditionalityProject } from '@features/resources/additionalities/api/activate-additionality-project';
import { EProjectTechnology } from '@global-types/api/enum';
import { useAuthorization } from '@lib/authorization';
import useStore from '@store/index';
import { checkAclFront } from '@utils/acl-front';
import { getCountryName } from '@utils/countries';
import { getCurrencyUnit } from '@utils/currency-units';
import {
	getFundedPercentage,
	interpolate,
} from '@utils/functions';
import clsx from 'clsx';
import { Text } from '../../../../../../components/v2/ui/Text';

const DEFAULT_COVER =
	'https://static.vecteezy.com/system/resources/thumbnails/028/142/383/small_2x/a-stadium-filled-with-excited-fans-a-football-field-in-the-foreground-background-with-empty-space-for-text-photo.jpg';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const AdditionalityProjectDetails = () => {
	/* ******************************** HOOKS ******************************* */
	const {
		additionalityProjectsPage,
		additionalityProjectsPageSize,
		additionalityProjectsQueryParams,
		setAdditionalitiesSelectedRow,
	} = useStore();

	const { mutate: mutateActivateAdditionalityProject } =
		useActivateAdditionalityProject(
			additionalityProjectsPage,
			additionalityProjectsPageSize,
			additionalityProjectsQueryParams,
			''
		);

	const { t } = useTranslation();

	const { ref: refCover } = useElementSize();

	const isSmall = useMediaQuery('(max-width: 768px)');

	const { ref } = useElementSize();

	const { projectId } = useParams();

	const { checkRole, checkAcls, role } =
		useAuthorization();

	const icon = <IconInfoCircle />;

	const [isExpanded, setIsExpanded] = useState(false);

	const { data, isFetching, isError, contactProducer } =
		useAdditionalityProject({
			projectId: projectId ? projectId : '',
		});

	if (isError) {
		return (
			<Box className={classes.container} ref={ref}>
				<Paper className={classes.paperError}>
					<Alert
						variant="light"
						color="error"
						title={t('errors.invalidProjectIdTitle')}
						icon={icon}
					>
						{t('errors.invalidProjectId')}
					</Alert>
				</Paper>
			</Box>
		);
	}

	if (isFetching) {
		return (
			<Box className={classes.containerSkeletons}>
				<Stack gap={'25px'}>
					<Skeleton
						height={'300px'}
						width={'100%'}
						radius="md"
					/>
					<Skeleton
						height={'20px'}
						width={'200px'}
						radius="md"
					/>
					<Skeleton
						height={'20px'}
						width={'100%'}
						radius="md"
					/>
					<Skeleton
						height={'20px'}
						width={'50%'}
						radius="md"
					/>
					<Skeleton
						height={'20px'}
						width={'50%'}
						radius="md"
					/>
					<Skeleton
						height={'20px'}
						width={'50%'}
						radius="md"
					/>
					<Skeleton
						height={'20px'}
						width={'50%'}
						radius="md"
					/>
				</Stack>
			</Box>
		);
	}

	const additionalityProject = data;

	if (!additionalityProject) return null;

	const {
		cover,
		name,
		technology,
		description,
		entity,
		location,
		financing,
		logo,
	} = additionalityProject;

	return (
		<Box className={classes.container} ref={ref}>
			<Box
				ref={refCover}
				style={{
					padding: '24px',
					display: 'flex',
					flex: '1',
					flexDirection: 'column',
					minHeight: '304px',
					maxHeight: 'fit-content',
					borderRadius: '32px',
					position: 'relative',
					backgroundSize: 'cover',
					backgroundPosition: 'right bottom',
					backgroundRepeat: 'no-repeat',
					backgroundImage:
						additionalityProject?.technologyData ==
						EProjectTechnology.PROJECT_FOUNDER
							? `url(${cover ?? DEFAULT_COVER})`
							: `
      linear-gradient(0deg, rgba(15, 23, 42, 0.00) 0%, #0F172A 104.61%),
      url(${cover ?? DEFAULT_COVER})
    `,
				}}
			>
				<Flex gap={'20px'}>
					{logo && (
						<Flex>
							<Image
								src={logo}
								w={'89px'}
								h={'89px'}
								radius={'xl'}
								style={{
									border: '4px solid #F0F3F8',
								}}
							/>
						</Flex>
					)}
					<Flex direction={'column'} flex={1} gap={'2px'}>
						{additionalityProject?.technologyData !==
							EProjectTechnology.PROJECT_FOUNDER && (
							<Flex wrap={'wrap'}>
								<Flex flex={1} wrap={'wrap'} gap={'20px'}>
									<Flex flex={1} direction={'column'}>
										<Flex>
											<Title
												order={4}
												c="var(--mantine-color-igluSurface-9)"
											>
												{name}
											</Title>
										</Flex>
										<Flex gap={'16px'}>
											<Group justify="center" gap={'5px'}>
												<Avatar
													radius={'xl'}
													src={`https://flagcdn.com/w40/${location?.countryCode?.toLowerCase()}.png`}
													size={18}
													style={{
														border: '1px solid #D7D6D6',
													}}
												>
													<IconQuestionMark size={18} />
												</Avatar>

												<Box>
													<Text
														variant={'caption'}
														c={
															'var(--mantine-color-igluSurface-9)'
														}
													>
														{getCountryName(
															location.countryCode
														)}
													</Text>
												</Box>
											</Group>
											<Group gap={'5px'}>
												<IconProjectLocation
													color={'#FDFDFD'}
												/>

												<Box>
													<Text
														variant={'caption'}
														c={
															'var(--mantine-color-igluSurface-9)'
														}
													>
														{location?.place ||
															NOT_APPLICABLE}
													</Text>
												</Box>
											</Group>

											{checkAclFront(
												ACLS.FRONT.ADDITIONALITIES
													.STATUS_TAG,
												role as ROLES
											) && (
												<StatusSwitch
													inActiveLabel={t(
														'global.inactive'
													)}
													activeLabel={t('global.active')}
													active={
														additionalityProject?.active
													}
													readonly={
														!checkRole([
															ROLES.ADMIN,
															ROLES.SUPERADMIN,
														])
													}
													onChange={async () => {
														setAdditionalitiesSelectedRow(
															additionalityProject
														);
														await mutateActivateAdditionalityProject(
															{
																active:
																	!additionalityProject.active,
																ids: [
																	additionalityProject.id,
																],
															}
														);
													}}
												/>
											)}
										</Flex>
									</Flex>
								</Flex>
								<Flex gap={'16px'} align={'center'}>
									<Flex direction={'column'} flex={1}>
										<Flex justify={'start'} flex={1}>
											<Title
												order={4}
												c="var(--mantine-color-igluSurface-9)"
												style={{
													whiteSpace: 'nowrap',
												}}
											>
												<NumberFormatter
													thousandSeparator=" "
													decimalSeparator=","
													value={
														financing?.pledged_euro || 0
													}
													decimalScale={2}
												/>
												<Text
													variant="subtitle1"
													span
													c={'#FDFDFD'}
												>
													{` ${getCurrencyUnit('EUR')}`}
												</Text>
											</Title>
										</Flex>
										{!checkRole([ROLES.STAFF]) && (
											<Flex
												justify={'end'}
												flex={1}
												gap={'10px'}
											>
												<Text
													variant="body1"
													c="var(--mantine-color-igluTextButton-9)"
													style={{
														whiteSpace: 'nowrap',
													}}
												>
													{t('global.fundedPart')}{' '}
												</Text>
												<Badge type={'SUCCESS'}>
													<Text variant={'button'}>
														<NumberFormatter
															thousandSeparator=" "
															decimalSeparator=","
															value={getFundedPercentage(
																financing?.pledged_euro,
																financing?.target_euro
															)}
															decimalScale={2}
														/>
														<Text size="11px" span>
															{' %'}
														</Text>
													</Text>
												</Badge>
											</Flex>
										)}
									</Flex>
									<Divider
										color="white"
										size="1px"
										orientation="vertical"
									/>
									{!checkRole([ROLES.STAFF]) && (
										<Flex direction={'column'} flex={1}>
											<Flex flex={1} wrap={'nowrap'}>
												<Title
													order={4}
													c="var(--mantine-color-igluSurface-9)"
													style={{
														whiteSpace: 'nowrap',
													}}
												>
													<NumberFormatter
														thousandSeparator=" "
														decimalSeparator=","
														value={
															financing?.target_euro || 0
														}
														decimalScale={2}
													/>
													<Text
														variant="subtitle1"
														span
														c={'#FDFDFD'}
													>
														{` ${getCurrencyUnit('EUR')}`}
													</Text>
												</Title>
											</Flex>
											<Flex flex={1}>
												<Text
													style={{
														whiteSpace: 'nowrap',
													}}
													variant="body1"
													c="var(--mantine-color-igluTextButton-9)"
												>
													{t('global.financialNeeds')}
												</Text>
											</Flex>
										</Flex>
									)}
								</Flex>
							</Flex>
						)}
						{additionalityProject?.technologyData ==
							EProjectTechnology.PROJECT_FOUNDER && (
							<Flex
								direction={'column'}
								justify={'flex-start'}
								align={'flex-start'}
								pb={'50px'}
							>
								<Title
									maw={'390px'}
									order={4}
									c={'var(--mantine-color-igluPrimary-9)'}
								>
									{additionalityProject.name}
								</Title>

								<Flex
									mt={'var(--mantine-spacing-sm)'}
									maw={'55%'}
									mb={'18px'}
								>
									<Text
										span
										variant={'body1'}
										c={
											'var(--mantine-color-igluTextPrimary-9)'
										}
									>
										{interpolate(
											t(
												'global.additionalityPoolDescription'
											),
											{
												nom_project:
													additionalityProject.name,
												organization:
													additionalityProject
														?.organization?.name ||
													NOT_APPLICABLE,
											}
										)}
									</Text>
								</Flex>

								{!checkRole([ROLES.STAFF]) && (
									<Text
										variant={'subtitle1'}
										c={
											'var(--mantine-color-igluTextPrimary-9)'
										}
									>
										{t('global.financialNeeds')}
										{' : '}
										<Text
											span
											variant="subtitle1"
											c="var(--mantine-color-igluPrimary-9)"
										>
											{financing.unlimited ? (
												t('global.unlimited')
											) : (
												<NumberFormatter
													thousandSeparator=" "
													value={
														financing?.target_euro ?? 0
													}
													suffix={
														' ' + getCurrencyUnit('EUR')
													}
													decimalScale={2}
												/>
											)}
										</Text>
									</Text>
								)}
								{!checkRole([ROLES.STAFF]) && (
									<Text
										variant={'subtitle1'}
										c={
											'var(--mantine-color-igluTextPrimary-9)'
										}
									>
										{t('global.fundedPart')}
										{' : '}
										<Text
											span
											variant={'subtitle1'}
											c={
												'var(--mantine-color-igluPrimary-9)'
											}
										>
											<NumberFormatter
												thousandSeparator=" "
												value={financing.pledged_euro}
												suffix={
													' ' + getCurrencyUnit('EUR')
												}
												decimalScale={2}
											/>
										</Text>
									</Text>
								)}
							</Flex>
						)}

						{additionalityProject?.technologyData !==
							EProjectTechnology.PROJECT_FOUNDER && (
							<Flex>
								<Flex flex={1} gap={'16px'}>
									<Text
										variant="body1"
										c="var(--mantine-color-igluTextButton-9)"
									>
										{t('global.type')}
										{' : '}
										<Text
											span
											variant="body2"
											c="var(--mantine-color-igluSurface-9)"
										>
											{t(technology)}
										</Text>
									</Text>
								</Flex>
								{checkAcls([
									ACLS.ADDITIONALITIES.UPDATE,
								]) &&
									!checkRole([
										ROLES.ORGANIZATION_ADMIN,
										ROLES.BRAND_ADMIN,
										ROLES.PRODUCER_ADMIN,
										ROLES.PRODUCER_STAFF,
									]) && (
										<Flex justify={'end'} flex={1}>
											<Button
												disabled
												variant={'primary'}
											>
												{t('global.edit')}
											</Button>
										</Flex>
									)}
							</Flex>
						)}
					</Flex>
				</Flex>
			</Box>

			<Flex
				style={{
					zIndex: '10',
					borderRadius: '32px',
					padding: '0px 30px',
				}}
				mt={'-60px'}
				direction={'column'}
				flex={1}
			>
				<Tabs
					defaultValue="generalInformations"
					classNames={classesTabs}
				>
					<Tabs.List>
						<Tabs.Tab value="generalInformations">
							{t('global.generalInformations')}
						</Tabs.Tab>
					</Tabs.List>
					{/* Infos générales */}
					<Tabs.Panel
						value="generalInformations"
						p={'20px'}
					>
						<Flex flex={1} gap={'24px'} wrap={'wrap'}>
							{additionalityProject?.technologyData !==
								EProjectTechnology.PROJECT_FOUNDER && (
								<Flex
									flex={1}
									gap={'24px'}
									direction={'column'}
									style={{
										flex: isSmall ? '1 1 100%' : 'none',
									}}
									bg={'#FDFDFD'}
								>
									<Flex
										className={classes.containerInfoBloc}
									>
										<Title
											order={5}
											c="var(--mantine-color-igluPrimary-9)"
											mb={'24px'}
										>
											{t('global.producerInfo')}
										</Title>
										<Flex
											style={{
												width: '100%',
											}}
											direction={'column'}
											gap={'8px'}
										>
											{contactProducer?.firstName &&
												contactProducer?.firstName !=
													NOT_APPLICABLE && (
													<Flex
														style={{
															width: '100%',
														}}
														flex={1}
													>
														<Flex
															className={
																classes.colProducerInfoTitle
															}
															flex={3}
														>
															<Text
																span
																variant="body2"
																c="var(--mantine-color-igluSecondary-9)"
															>
																{t('global.name')}
															</Text>
														</Flex>
														<Flex flex={5}>
															<Text
																span
																variant="body2-medium"
																c="var(--mantine-color-igluTextPrimary-9)"
															>
																{contactProducer
																	? `${contactProducer.firstName} ${contactProducer.lastName}`
																	: NOT_APPLICABLE}
															</Text>
														</Flex>
													</Flex>
												)}
											{entity?.name &&
												entity?.name !=
													NOT_APPLICABLE && (
													<Flex
														style={{
															width: '100%',
														}}
														flex={1}
													>
														<Flex
															className={
																classes.colProducerInfoTitle
															}
															flex={3}
														>
															<Text
																span
																variant="body2"
																c="var(--mantine-color-igluSecondary-9)"
															>
																{t('global.contact')}
															</Text>
														</Flex>
														<Flex flex={5}>
															<Text
																span
																variant="body2-medium"
																c="var(--mantine-color-igluTextPrimary-9)"
															>
																{entity.name
																	? entity.name
																	: NOT_APPLICABLE}
															</Text>
														</Flex>
													</Flex>
												)}
											<Flex
												style={{
													width: '100%',
												}}
												flex={1}
											>
												<Flex
													className={
														classes.colProducerInfoTitle
													}
													flex={3}
												>
													<Text
														span
														variant="body2"
														c="var(--mantine-color-igluSecondary-9)"
													>
														{t('global.location')}
													</Text>
												</Flex>
												<Flex
													align={'center'}
													gap={'5px'}
													flex={5}
												>
													<Avatar
														className={
															classes.logoCountry
														}
														radius={'xl'}
														src={`https://flagcdn.com/w40/${entity.location && entity.location.countryCode ? entity.location.countryCode.toLowerCase() : ''}.png`}
														size={'xs'}
													>
														<IconQuestionMark />
													</Avatar>

													<Text
														span
														variant="body2-medium"
														c="var(--mantine-color-igluTextPrimary-9)"
													>
														{getCountryName(
															entity.location &&
																entity.location
																	.countryCode
																? entity.location
																		.countryCode
																: ''
														)}
													</Text>
												</Flex>
											</Flex>
											{entity?.legalIdentifier &&
												entity?.legalIdentifier !=
													NOT_APPLICABLE && (
													<Flex
														style={{
															width: '100%',
														}}
														flex={1}
													>
														<Flex
															flex={3}
															className={
																classes.colProducerInfoTitle
															}
														>
															<Text
																span
																variant="body2"
																c="var(--mantine-color-igluSecondary-9)"
															>
																{t(
																	'projectDetails.producerLegalIdentifier'
																)}
															</Text>
														</Flex>
														<Flex flex={5}>
															<Text
																span
																variant="body2-medium"
																c="var(--mantine-color-igluTextPrimary-9)"
															>
																{entity &&
																entity.legalIdentifier
																	? entity.legalIdentifier
																	: NOT_APPLICABLE}
															</Text>
														</Flex>
													</Flex>
												)}
										</Flex>
									</Flex>
								</Flex>
							)}
							<Flex
								bg={'#FDFDFD'}
								flex={2}
								direction={'column'}
								gap={'24px'}
							>
								<Flex
									className={clsx(
										additionalityProject?.technologyData !==
											EProjectTechnology.PROJECT_FOUNDER
											? classes.containerBloc
											: classes.containerBlocWithoutShadow
									)}
								>
									{additionalityProject?.technologyData !==
										EProjectTechnology.PROJECT_FOUNDER && (
										<Title
											order={5}
											c="var(--mantine-color-igluPrimary-9)"
											mb={'24px'}
										>
											{t('global.description')}
										</Title>
									)}
									<div>
										{description ? (
											additionalityProject?.technologyData ==
											EProjectTechnology.PROJECT_FOUNDER ? (
												<DescriptionRenderer
													description={description}
												/>
											) : (
												<Spoiler
													onExpandedChange={(opened) =>
														setIsExpanded(opened)
													}
													maxHeight={120}
													showLabel={t('global.showMore')}
													hideLabel={t('global.hide')}
													styles={{
														control: {
															fontFamily:
																'InterDisplay-SemiBold',
															color:
																'var(--mantine-color-igluPrimary-9)',
														},
													}}
												>
													<DescriptionRenderer
														description={description}
													/>
													{!isExpanded && (
														<div
															style={{
																position: 'absolute',
																bottom: 0,
																left: 0,
																width: '100%',
																height: '40px',
															}}
														/>
													)}
												</Spoiler>
											)
										) : (
											<Text
												span
												variant="body1"
												c="var(--mantine-color-igluTextPrimary-9)"
											>
												{NOT_APPLICABLE}
											</Text>
										)}
									</div>
								</Flex>
							</Flex>
						</Flex>
					</Tabs.Panel>
				</Tabs>
			</Flex>
		</Box>
	);
};

export default AdditionalityProjectDetails;
